import Page from '../../../../components/PageWrapper';
import { useMemo, useState } from 'react';
import GTable from '../../../../components/elements/GTable';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ITableAction, ITablePaginationData } from '../../../../components/elements/GTable/GTable';
import useModal from '../../../../hooks/modalHook';
import { capitalizeSentence, formatNumberValue } from '../../../../utils/miscFunctions';
import useRoute from '../../../../hooks/routesHook';
import { EllipsisVerticalIcon } from '@heroicons/react/24/solid';
import { useDispatch } from 'react-redux';
import moment from 'moment';
import PopOnMe from '../../../../components/elements/poponme/PopOnMe';
import { getAllDisbursements } from '../../../../services/disbursements';
import useDisbursements from '../../../../hooks/disbursementHook';
import { Eye } from 'lucide-react';


const Disbursement = (): JSX.Element => {
    const page = 'disbursement';
    const dispatch = useDispatch();
    const [disbursements, setDisbursements] = useState<any[]>([]);
    const [searchParams, setSearchParams] = useSearchParams();
    const [currentPage, setCurrentPage] = useState<number>(searchParams.get('page') ? Number(searchParams.get('page')) : 0);
    const [loading, setLoading] = useState<boolean>(true);
    const [disbursementsPaginationData, setDisbursementsPaginationData] = useState<ITablePaginationData>();
    const [showPerPage, setShowPerPage] = useState<number>(100);
    const { ModalComponent, updateModalStates, closeModal } = useModal();
    const navigate = useNavigate();
    const { getDashboardBase } = useRoute();
    const invalidateQueries = useQueryClient();


    useQuery({
        queryKey: ['fetchAllDisbursements', currentPage, showPerPage],
        queryFn: () => fetchAllDisbursements()
    });

    const fetchAllDisbursements = async () => {
        try {
            const response: any = await getAllDisbursements(currentPage, showPerPage);

            if (response) {
                const _disbursements = response?.content.map((_disbursement: any) => _disbursement);
                setDisbursements(_disbursements);

                const from = response.pageable?.offset + 1;
                const to =
                    from + response.size > response.totalElements
                        ? response.totalElements
                        : from + response.size - 1;

                setDisbursementsPaginationData({
                    ...disbursementsPaginationData,
                    per_page: response.size ?? 0,
                    total: response.totalElements ?? 0,
                    totalPages: response.totalPages ?? 0,
                    from,
                    to
                });
            }
        } catch (error) {
        } finally {
            setLoading(false);
        }


    };

    const getTableRowActions = (disbursement: any) => {
        const tableActions: ITableAction[] = [
            {
                label: 'View',
                icon:<Eye className="h-4 w-4"/>,
                callback: () => navigate(`${getDashboardBase()}/disbursements/${disbursement?.id}`)
            },
        ];

        return tableActions;
    }


    const handlePageChange = (page: number) => {
        setCurrentPage(page);
        setSearchParams({ page: String(page) });
    };

    const { columns } = useDisbursements({ tableRowActions: getTableRowActions });



    return (
        <Page title={`${capitalizeSentence(page)}`}>
            <div className="bg-white dark:bg-darkBg w-full p-5 text-sm min-h-full rounded-lg shadow-md">

                <div className="">
                    <div className="inline-flex items-center gap-2 mb-2">
                        <div className="h-8 w-2 bg-gradient-to-b from-yellow-500 to-yellow-700 rounded"></div>
                        <h2 className="font-semibold text-3xl text-gray-900 dark:text-darkBg-mainText">{capitalizeSentence(page)}s</h2>
                    </div>
                    
                    <p className="text-sm text-slate-400 dark:text-darkBg-subText">
                        View a list of all {page}s and their details{' '}
                    </p>
                </div>

                <div className="overflow-x-auto mt-4">
                    <GTable
                        columns={columns}
                        loading={loading}
                        data={disbursements}
                        classes="h-fit"
                        showSearch={false}
                        tableType={'disbursement'}
                        paginationData={disbursementsPaginationData}
                        onPageChange={handlePageChange}
                        showPerPage={showPerPage}
                        stickyHeader
                        onShowPerPageChange={(page: number) => setShowPerPage(page)}
                        onRowClick={(row: any) => navigate(`${getDashboardBase()}/disbursements/${row.id}`)}
                    />
                </div>

            </div>
            <ModalComponent />
        </Page>
    );
};

export default Disbursement;
